import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Target, BarChart3, Shield, CheckCircle2, AlertCircle, Sliders, MonitorPlay, Activity, Repeat, ShieldCheck, Menu, X, Lock } from 'lucide-react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { mockData } from '../utils/mockData';
import '../styles/LandingPage.css';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ src: '', alt: '' });

  const handleGetStarted = (plan) => {
    // Open the respective Stripe payment link based on the plan
    let paymentLink;
    switch(plan) {
      case 'weekly':
        paymentLink = mockData.stripeLinks.weekly;
        break;
      case 'monthly':
        paymentLink = mockData.stripeLinks.monthly;
        break;
      case 'lifetime':
        paymentLink = mockData.stripeLinks.lifetime;
        break;
      default:
        paymentLink = mockData.stripeLinks.monthly; // Default to monthly
    }
    
    // Check if it's still a placeholder
    if (paymentLink.includes('REPLACE_WITH')) {
      alert('Please add your Stripe payment links in /app/frontend/src/utils/mockData.js');
      return;
    }
    
    window.open(paymentLink, '_blank');
  };

  const scrollToSection = (sectionClass) => {
    const section = document.querySelector(`.${sectionClass}`);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false); // Close menu after clicking
  };

  const scrollToPricing = () => {
    scrollToSection('pricing-section');
  };

  const openLightbox = (src, alt) => {
    setLightboxImage({ src, alt });
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage({ src: '', alt: '' });
    document.body.style.overflow = 'unset'; // Re-enable scroll
  };

  // Close lightbox on ESC key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [lightboxOpen]);

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src={mockData.logoUrl} alt="XMC Algo" className="logo-image" />
              <span className="logo-text">XMC Algo</span>
            </div>
            <div className="nav-wrapper">
              <nav className="nav-menu">
                <button onClick={() => scrollToSection('about-section')} className="nav-link">About</button>
                <button onClick={() => scrollToSection('how-it-works-section')} className="nav-link">How It Works</button>
                <button onClick={() => scrollToSection('features-section')} className="nav-link">Features</button>
              </nav>
              <Button onClick={scrollToPricing} className="cta-button">
                Pricing
              </Button>
            </div>
            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-button" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <button onClick={() => scrollToSection('about-section')} className="mobile-menu-link">About</button>
            <button onClick={() => scrollToSection('how-it-works-section')} className="mobile-menu-link">How It Works</button>
            <button onClick={() => scrollToSection('features-section')} className="mobile-menu-link">Features</button>
            <button onClick={scrollToPricing} className="mobile-menu-link mobile-menu-cta">Pricing</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Cleaner trend signals.<br />
                Less noise.<br />
                More structure.
              </h1>
              <p className="hero-description">
                XMC Algo is a trend-following indicator designed to help traders spot cleaner setups 
                and get better confirmation on chart. Simple, effective, and built for trending markets.
              </p>
              <div className="hero-cta">
                <Button onClick={scrollToPricing} size="lg" className="cta-button-large">
                  See Pricing
                </Button>
              </div>
            </div>
            <div className="hero-devices">
              <img src="https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/43lpsnug_Screenshot%202026-03-11%20222647.png" alt="XMC Algo on multiple devices" />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What is XMC Algo?</h2>
            <p className="section-description">
              A professional-grade trend-following indicator that cuts through market noise 
              to deliver clear, actionable signals for confident trading decisions.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title-center">How It Works</h2>
          <div className="how-it-works-grid">
            <div className="how-step">
              <div className="step-number">01</div>
              <h3 className="step-title">Add it to your chart</h3>
              <p className="step-description">
                Once access is granted, open TradingView and add XMC Algo from your invite-only scripts.
              </p>
            </div>
            <div className="how-step">
              <div className="step-number">02</div>
              <h3 className="step-title">Follow the main signals</h3>
              <p className="step-description">
                The larger BUY and SELL labels are the main confirmation signals, built to highlight cleaner trend entries.
              </p>
            </div>
            <div className="how-step">
              <div className="step-number">03</div>
              <h3 className="step-title">Use continuation signals</h3>
              <p className="step-description">
                The smaller arrow signals appear more often and can help spot continuation or re-entry opportunities while the trend is already moving.
              </p>
            </div>
            <div className="how-step">
              <div className="step-number">04</div>
              <h3 className="step-title">Manage risk properly</h3>
              <p className="step-description">
                Use the signals as confirmation, not autopilot. Set your own stop loss, take profit, and risk before entering any trade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Market Conditions Section - Chart Examples */}
      <section className="market-conditions-section">
        <div className="container">
          <div className="market-conditions-content">
            <div className="market-icon-wrapper">
              <TrendingUp size={64} />
            </div>
            <h2 className="section-title-center">Best Used in Trending Markets</h2>
            <p className="market-description">
              XMC Algo is optimized for trending market conditions where clear directional movement exists. 
              While it can work in various environments, you'll see the strongest performance during established 
              uptrends or downtrends. Ranging or highly volatile sideways markets may produce less reliable signals.
            </p>
          </div>

          {/* Chart Examples */}
          <div className="chart-examples">
            <div className="chart-example">
              <img 
                src="https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/oh32w1cl_Screenshot%202026-04-20%20at%201.29.03%E2%80%AFam.png" 
                alt="Bitcoin chart example"
                className="chart-image"
                onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/oh32w1cl_Screenshot%202026-04-20%20at%201.29.03%E2%80%AFam.png', 'Bitcoin chart example')}
              />
              <div className="chart-caption">
                <h4 className="chart-title">BTCUSD · Bitcoin</h4>
                <p className="chart-description">Example of XMC Algo highlighting trend continuation and main signal entries.</p>
              </div>
            </div>

            <div className="chart-example">
              <img 
                src="https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/1hotq5i4_Screenshot%202026-04-20%20at%201.30.04%E2%80%AFam.png" 
                alt="Gold chart example"
                className="chart-image"
                onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/1hotq5i4_Screenshot%202026-04-20%20at%201.30.04%E2%80%AFam.png', 'Gold chart example')}
              />
              <div className="chart-caption">
                <h4 className="chart-title">XAUUSD · Gold</h4>
                <p className="chart-description">Example of cleaner confirmation in a trending market.</p>
              </div>
            </div>

            <div className="chart-example">
              <img 
                src="https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/svu6nqou_Screenshot%202026-04-20%20at%201.30.58%E2%80%AFam.png" 
                alt="S&P500 chart example"
                className="chart-image"
                onClick={() => openLightbox('https://customer-assets.emergentagent.com/job_algo-trading-hub-30/artifacts/svu6nqou_Screenshot%202026-04-20%20at%201.30.58%E2%80%AFam.png', 'S&P500 chart example')}
              />
              <div className="chart-caption">
                <h4 className="chart-title">SPY · S&P500</h4>
                <p className="chart-description">Example of the indicator across a major traditional market.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title-center">Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Target />
              </div>
              <h3 className="feature-title">Cleaner Setups</h3>
              <p className="feature-description">
                Filters out market noise to highlight high-probability trading opportunities.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <TrendingUp />
              </div>
              <h3 className="feature-title">Better Confirmation</h3>
              <p className="feature-description">
                Get clear trend confirmation signals to validate your trading decisions.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <BarChart3 />
              </div>
              <h3 className="feature-title">Reduced Noise</h3>
              <p className="feature-description">
                Sophisticated algorithms eliminate false signals and market clutter.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Shield />
              </div>
              <h3 className="feature-title">Simple to Use</h3>
              <p className="feature-description">
                No complex configurations. Clean interface designed for practical trading.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Sliders />
              </div>
              <h3 className="feature-title">Highly Customizable</h3>
              <p className="feature-description">
                Adjust filters, signal settings, and display options to match your trading style.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Lock />
              </div>
              <h3 className="feature-title">100% Non-Repainting</h3>
              <p className="feature-description">
                Signals are locked in on bar close. What you see in backtests is exactly what you get in real-time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title-center">Choose Your Plan</h2>
          <div className="pricing-grid">
            <Card className="pricing-card">
              <CardHeader>
                <CardTitle>Weekly</CardTitle>
                <CardDescription>Perfect for trying it out</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="price">
                  <span className="price-amount">$14</span>
                  <span className="price-period">/week</span>
                </div>
                <ul className="pricing-features">
                  <li><CheckCircle2 size={16} /> Full indicator access</li>
                  <li><CheckCircle2 size={16} /> All chart timeframes</li>
                  <li><CheckCircle2 size={16} /> Main + continuation signals</li>
                  <li><CheckCircle2 size={16} /> Weekly renewal</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button onClick={() => handleGetStarted('weekly')} variant="outline" className="pricing-button">
                  Get Started
                </Button>
              </CardFooter>
            </Card>

            <Card className="pricing-card pricing-card-featured">
              <div className="featured-badge">Most Popular</div>
              <CardHeader>
                <CardTitle>Monthly</CardTitle>
                <CardDescription>Best value for active traders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="price">
                  <span className="price-amount">$49</span>
                  <span className="price-period">/month</span>
                </div>
                <ul className="pricing-features">
                  <li><CheckCircle2 size={16} /> Full indicator access</li>
                  <li><CheckCircle2 size={16} /> All chart timeframes</li>
                  <li><CheckCircle2 size={16} /> Main + continuation signals</li>
                  <li><CheckCircle2 size={16} /> Ongoing updates</li>
                  <li><CheckCircle2 size={16} /> Priority support</li>
                  <li><CheckCircle2 size={16} /> Monthly renewal</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button onClick={() => handleGetStarted('monthly')} className="pricing-button cta-button">
                  Get Started
                </Button>
              </CardFooter>
            </Card>

            <Card className="pricing-card pricing-card-sale">
              <div className="sale-badge">Limited Time Offer</div>
              <CardHeader>
                <CardTitle>Lifetime</CardTitle>
                <CardDescription>One-time payment, forever access</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="price">
                  <span className="price-original">$599</span>
                  <span className="price-amount">$399</span>
                  <span className="price-period">one-time</span>
                </div>
                <ul className="pricing-features">
                  <li><CheckCircle2 size={16} /> Full indicator access</li>
                  <li><CheckCircle2 size={16} /> All chart timeframes</li>
                  <li><CheckCircle2 size={16} /> Main + continuation signals</li>
                  <li><CheckCircle2 size={16} /> All future updates included</li>
                  <li><CheckCircle2 size={16} /> Priority support</li>
                  <li><CheckCircle2 size={16} /> One-time payment</li>
                  <li><CheckCircle2 size={16} /> No recurring fees</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button onClick={() => handleGetStarted('lifetime')} className="pricing-button pricing-button-sale">
                  Get Started
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* Refund & Cancellation Policy */}
      <section className="refund-policy-section">
        <div className="container">
          <div className="refund-policy-content">
            <Accordion type="single" collapsible className="refund-accordion">
              <AccordionItem value="refund-policy">
                <AccordionTrigger className="refund-trigger">Refund & Cancellation Policy</AccordionTrigger>
                <AccordionContent className="refund-content">
                  <ul className="refund-list">
                    <li>All sales are final. No refunds will be issued.</li>
                    <li>Weekly and monthly subscriptions can be cancelled at any time.</li>
                    <li>If cancelled, access will remain active until the end of the current billing period.</li>
                    <li>Lifetime access is a one-time payment with no recurring fees.</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="disclaimer-section">
        <div className="container">
          <div className="disclaimer-content">
            <AlertCircle className="disclaimer-icon" />
            <h3 className="disclaimer-title">Important Disclaimer</h3>
            <p className="disclaimer-text">
              XMC Algo is a technical analysis tool designed to assist with trading decisions. 
              <strong> This is not financial advice.</strong> Trading involves substantial risk, 
              and there are no guarantees of profit. Past performance does not indicate future results. 
              Always conduct your own research and consider your risk tolerance before trading.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title-center">Frequently Asked Questions</h2>
          <div className="faq-content">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>What platform does XMC Algo work on?</AccordionTrigger>
                <AccordionContent>
                  XMC Algo is built for TradingView.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>How do I get access after buying?</AccordionTrigger>
                <AccordionContent>
                  After purchase, submit your TradingView username through the access form. Once approved, the indicator will appear in your invite-only scripts.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>Does it work on all markets?</AccordionTrigger>
                <AccordionContent>
                  It can be used across different markets, but it works best in trending conditions.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>Does it work on all timeframes?</AccordionTrigger>
                <AccordionContent>
                  Yes, but results can vary depending on the market and timeframe being used.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>What do the signals mean?</AccordionTrigger>
                <AccordionContent>
                  The main BUY/SELL signals are the stronger confirmation signals. The smaller continuation signals are more aggressive re-entry signals.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger>Is this financial advice?</AccordionTrigger>
                <AccordionContent>
                  No. XMC Algo is a charting and confirmation tool only.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger>Can I cancel anytime?</AccordionTrigger>
                <AccordionContent>
                  Yes. Weekly and monthly plans can be cancelled through Stripe.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8">
                <AccordionTrigger>How is lifetime access billed?</AccordionTrigger>
                <AccordionContent>
                  Lifetime access is a one-time payment with no recurring fees.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9">
                <AccordionTrigger>Will I get future updates?</AccordionTrigger>
                <AccordionContent>
                  Yes. Monthly and lifetime plans include ongoing updates, with lifetime including all future updates.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10">
                <AccordionTrigger>Is it beginner friendly?</AccordionTrigger>
                <AccordionContent>
                  Yes, but it should still be used with basic risk management and your own judgment.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2 className="final-cta-title">Ready to Trade with Clarity?</h2>
            <p className="final-cta-description">
              Join traders who are cutting through the noise and making more confident decisions.
            </p>
            <Button onClick={() => handleGetStarted('monthly')} size="lg" className="cta-button-large">
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={mockData.logoUrl} alt="XMC Algo" className="footer-logo-image" />
              <span className="footer-logo-text">XMC Algo</span>
            </div>
            <p className="footer-text">
              © 2024 XMC Algo. Premium trading indicators for serious traders.
            </p>
          </div>
        </div>
      </footer>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <X size={32} />
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImage.src} alt={lightboxImage.alt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
